import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Users, GitMerge, ArrowRight, AlertTriangle, ChevronLeft, UserCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Duplicate Skater Finder — lists candidate clusters across every
 * competition the caller can access, drills into one cluster at a time
 * for a side-by-side view, and runs the merge through
 * /api/skaters/duplicates/merge.
 */
export default function SkaterDeduplicationPage() {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState(null);
  const [activeCluster, setActiveCluster] = useState(null);
  const [keeperId, setKeeperId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  // Skater IDs picked via checkbox for the "These are different people"
  // action. Empty until at least 2 are ticked.
  const [uniqueSelection, setUniqueSelection] = useState([]);
  const [markingUnique, setMarkingUnique] = useState(false);
  const [confirmUniqueOpen, setConfirmUniqueOpen] = useState(false);
  // Audit panel: every confirmed-unique pair visible to the caller.
  // Null = not yet loaded.
  const [uniquePairs, setUniquePairs] = useState(null);
  const [revokingPair, setRevokingPair] = useState(null);

  const loadClusters = async () => {
    try {
      const res = await axios.get(`${API}/skaters/duplicates/scan`, { withCredentials: true });
      setClusters(res.data.clusters || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load duplicate clusters');
    }
  };

  const loadUniquePairs = async () => {
    try {
      const res = await axios.get(`${API}/skaters/duplicates/confirmed-unique`,
        { withCredentials: true });
      setUniquePairs(res.data.pairs || []);
    } catch (err) {
      // Non-fatal — the panel just stays hidden.
      console.warn('Failed to load confirmed-unique pairs:', err);
      setUniquePairs([]);
    }
  };

  useEffect(() => { loadClusters(); loadUniquePairs(); }, []);

  const revokeUniquePair = async (pair_key, label) => {
    if (!window.confirm(`Revoke "${label}"? This pair will be eligible to re-cluster on the next scan.`)) {
      return;
    }
    setRevokingPair(pair_key);
    try {
      await axios.delete(`${API}/skaters/duplicates/mark-unique/${pair_key}`,
        { withCredentials: true });
      toast.success('Pair clearance revoked');
      await Promise.all([loadClusters(), loadUniquePairs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    } finally {
      setRevokingPair(null);
    }
  };

  const openCluster = async (cid) => {
    try {
      const res = await axios.get(`${API}/skaters/duplicates/cluster/${cid}`,
        { withCredentials: true });
      setActiveCluster(res.data);
      setKeeperId(res.data.suggested_keeper_id);
      setUniqueSelection([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load cluster detail');
    }
  };

  const toggleUniqueSelect = (sid) => {
    setUniqueSelection((curr) =>
      curr.includes(sid) ? curr.filter((x) => x !== sid) : [...curr, sid]
    );
  };

  const runMarkUnique = async () => {
    if (uniqueSelection.length < 2) return;
    setMarkingUnique(true);
    try {
      const res = await axios.post(`${API}/skaters/duplicates/mark-unique`, {
        skater_ids: uniqueSelection,
      }, { withCredentials: true });
      const newPairs = res.data.pairs_written?.length || 0;
      const existing = res.data.pairs_already_existing?.length || 0;
      const parts = [];
      if (newPairs) parts.push(`${newPairs} pair${newPairs === 1 ? '' : 's'} confirmed unique`);
      if (existing) parts.push(`${existing} already on file`);
      toast.success(parts.join(' · ') || 'Marked as unique');
      setConfirmUniqueOpen(false);
      // Re-load: cluster may have disappeared (all pairs cleared) — fall
      // back to the index view if the current cluster_id is gone.
      const scanRes = await axios.get(`${API}/skaters/duplicates/scan`, { withCredentials: true });
      const list = scanRes.data.clusters || [];
      setClusters(list);
      await loadUniquePairs();
      const still = list.find((c) => c.cluster_id === activeCluster.cluster_id);
      if (still) {
        const detail = await axios.get(`${API}/skaters/duplicates/cluster/${activeCluster.cluster_id}`,
          { withCredentials: true });
        setActiveCluster(detail.data);
        setUniqueSelection([]);
      } else {
        setActiveCluster(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mark-unique failed');
    } finally {
      setMarkingUnique(false);
    }
  };

  const runMerge = async () => {
    if (!activeCluster || !keeperId) return;
    setMerging(true);
    try {
      const losers = activeCluster.records
        .map(r => r.skater_id)
        .filter(id => id !== keeperId);
      const res = await axios.post(`${API}/skaters/duplicates/merge`, {
        keeper_id: keeperId,
        loser_ids: losers,
      }, { withCredentials: true });
      toast.success(
        `Merged ${losers.length} record${losers.length === 1 ? '' : 's'} — `
        + `${res.data.entries_repointed} entr${res.data.entries_repointed === 1 ? 'y' : 'ies'} re-pointed`
      );
      setActiveCluster(null);
      setConfirmOpen(false);
      await loadClusters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  // Hooks must be unconditional — compute confirmed-pair lookup at the
  // top level so the index-view early return below doesn't change the
  // hook ordering across renders.
  const clearedPairs = useMemo(() => {
    const set = new Set();
    (activeCluster?.confirmed_unique_pairs || []).forEach((p) => set.add(p.pair_key));
    return set;
  }, [activeCluster]);

  // ---- INDEX VIEW ----
  if (!activeCluster) {
    return (
      <div className="space-y-5" data-testid="dedup-page">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-[#2D4A3E]" />
          <div>
            <h1 className="text-2xl font-bold text-[#2D4A3E]">Duplicate Skater Finder</h1>
            <p className="text-sm text-stone-600">
              Candidate clusters detected across every competition you can access. Each cluster
              shows two or more skater records that may represent the same real person.
            </p>
          </div>
        </div>

        {clusters === null && (
          <p className="text-sm text-stone-500">Loading…</p>
        )}

        {clusters && clusters.length === 0 && (
          <Card className="p-6 text-center text-stone-600">
            <p className="text-base font-medium text-[#2D4A3E] mb-1">No duplicates found.</p>
            <p className="text-sm">Every skater in your competitions has a single record.</p>
          </Card>
        )}

        {clusters && clusters.length > 0 && (
          <div className="space-y-3">
            {clusters.map((c) => (
              <Card
                key={c.cluster_id}
                className="p-4 hover:shadow-md transition cursor-pointer"
                onClick={() => openCluster(c.cluster_id)}
                data-testid={`cluster-row-${c.cluster_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-stone-800">
                      {c.records[0].first_name} {c.records[0].last_name}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {c.records.map(r => r.club_name || 'Unknown club').join(' · ')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{c.size} records</Badge>
                    <Button variant="ghost" size="sm">
                      Review <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Confirmed Unique Pairs — audit panel of past clearances. */}
        {uniquePairs && uniquePairs.length > 0 && (
          <div className="space-y-3 pt-6" data-testid="confirmed-unique-panel">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-700" />
              <h2 className="text-lg font-semibold text-[#2D4A3E]">
                Confirmed Unique Pairs
              </h2>
              <Badge variant="outline"
                className="border-emerald-300 bg-emerald-50 text-emerald-800">
                {uniquePairs.length}
              </Badge>
            </div>
            <p className="text-xs text-stone-500 -mt-2">
              Pairs you've confirmed as different people. They won't re-cluster
              as duplicates. Revoke a clearance if you change your mind.
            </p>
            <div className="space-y-2">
              {uniquePairs.map((p) => (
                <Card
                  key={p.pair_key}
                  className="p-3 flex items-center justify-between"
                  data-testid={`unique-pair-row-${p.pair_key}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-stone-800 truncate">
                      <span className={p.skater_a.merged ? 'line-through text-stone-500' : 'font-medium'}>
                        {p.name_a}
                      </span>
                      <span className="text-stone-400 mx-2">vs</span>
                      <span className={p.skater_b.merged ? 'line-through text-stone-500' : 'font-medium'}>
                        {p.name_b}
                      </span>
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Confirmed by {p.confirmed_by_name || p.confirmed_by || 'unknown'}
                      {p.confirmed_at && (
                        <> · {new Date(p.confirmed_at).toLocaleString('en-NZ', {
                          dateStyle: 'medium', timeStyle: 'short',
                        })}</>
                      )}
                      {p.note && <span className="italic"> · "{p.note}"</span>}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => revokeUniquePair(p.pair_key, `${p.name_a} vs ${p.name_b}`)}
                    disabled={revokingPair === p.pair_key}
                    className="text-stone-700"
                    data-testid={`revoke-unique-btn-${p.pair_key}`}
                  >
                    <Undo2 className="h-4 w-4 mr-1.5" />
                    {revokingPair === p.pair_key ? 'Revoking…' : 'Revoke'}
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- CLUSTER DETAIL VIEW ----
  const keeperRecord = activeCluster.records.find(r => r.skater_id === keeperId);
  const losers = activeCluster.records.filter(r => r.skater_id !== keeperId);
  const totalEntries = activeCluster.records.reduce((n, r) => n + (r.entries?.length || 0), 0);

  const sortedPairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);

  // Per-record: is this record already confirmed unique against EVERY
  // other record in the cluster? If so, render a clear pill so the
  // user knows it's been cleared end-to-end (though the cluster
  // remains because at least one other pair is still suspect).
  const otherRecords = (sid) => activeCluster.records.filter(r => r.skater_id !== sid);
  const allClearedFor = (sid) =>
    otherRecords(sid).every(r => clearedPairs.has(sortedPairKey(sid, r.skater_id)));

  const selectedNames = uniqueSelection.map((sid) => {
    const r = activeCluster.records.find((x) => x.skater_id === sid);
    return r ? `${r.first_name} ${r.last_name}` : sid;
  });

  return (
    <div className="space-y-5" data-testid="dedup-cluster-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setActiveCluster(null)}
          data-testid="back-to-clusters">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to all duplicates
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-[#2D4A3E]">
          {activeCluster.records[0].first_name} {activeCluster.records[0].last_name}
        </h1>
        <p className="text-sm text-stone-600 mt-1">
          {activeCluster.records.length} records, {totalEntries} total
          entr{totalEntries === 1 ? 'y' : 'ies'} across them. Pick which record to
          keep — all entries from the others will be re-pointed at the keeper.
        </p>
      </div>

      {!activeCluster.can_merge && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>{activeCluster.blocked_reason}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {activeCluster.records.map((r) => {
          const isKeeper = r.skater_id === keeperId;
          const isSelectedUnique = uniqueSelection.includes(r.skater_id);
          const isFullyCleared = allClearedFor(r.skater_id);
          return (
            <Card
              key={r.skater_id}
              className={`p-4 border-2 ${isKeeper ? 'border-[#2D4A3E] bg-[#2D4A3E]/5' : 'border-stone-200'}`}
              data-testid={`record-card-${r.skater_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="keeper"
                    checked={isKeeper}
                    onChange={() => setKeeperId(r.skater_id)}
                    data-testid={`keeper-radio-${r.skater_id}`}
                  />
                  <label className="font-semibold text-stone-800">
                    {isKeeper ? 'KEEP this record' : 'Merge this into keeper'}
                  </label>
                </div>
                <Badge variant="outline">{r.fill_rate} fields populated</Badge>
              </div>
              <div className="mb-3 flex items-center gap-2 rounded-md bg-stone-50 border border-stone-200 px-2.5 py-1.5">
                <Checkbox
                  id={`unique-${r.skater_id}`}
                  checked={isSelectedUnique}
                  onCheckedChange={() => toggleUniqueSelect(r.skater_id)}
                  data-testid={`unique-checkbox-${r.skater_id}`}
                />
                <label htmlFor={`unique-${r.skater_id}`}
                  className="text-xs text-stone-700 cursor-pointer select-none">
                  Different person from the other tick&#8209;boxed records
                </label>
                {isFullyCleared && (
                  <Badge variant="outline"
                    className="ml-auto border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]"
                    data-testid={`cleared-pill-${r.skater_id}`}>
                    <UserCheck className="h-3 w-3 mr-1" />
                    Confirmed unique vs others
                  </Badge>
                )}
              </div>
              <dl className="text-sm space-y-1">
                <Row label="Name">{r.first_name} {r.last_name}</Row>
                <Row label="Gender">{r.gender || '—'}</Row>
                <Row label="Club">{r.club_name || '—'}</Row>
                <Row label="DOB">{r.date_of_birth || '—'}</Row>
                <Row label="Age">{r.age ?? '—'}</Row>
                <Row label="Over 18">{r.over_18 === true ? 'Yes' : r.over_18 === false ? 'No' : '—'}</Row>
                <Row label="NZFRS #">{r.nzfrs_number || '—'}</Row>
                <Row label="Artistic #">{r.artistic_number || '—'}</Row>
                <Row label="Skater ID">
                  <code className="text-xs text-stone-500">{r.skater_id}</code>
                </Row>
              </dl>
              <div className="mt-3 pt-3 border-t border-stone-200">
                <p className="text-xs text-stone-500 mb-1.5">
                  Entries ({r.entries?.length || 0}):
                </p>
                {(r.entries || []).length === 0 && (
                  <p className="text-xs text-stone-400">No entries reference this record.</p>
                )}
                <ul className="text-xs space-y-0.5">
                  {(r.entries || []).map((e) => (
                    <li key={e.entry_id}>
                      <span className="text-stone-700">{e.competition_name}</span>
                      <span className="text-stone-400"> / {e.grade_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!activeCluster.can_merge || losers.length === 0}
          className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
          data-testid="merge-btn"
        >
          <GitMerge className="mr-2 h-4 w-4" />
          Merge {losers.length} record{losers.length === 1 ? '' : 's'} into keeper
        </Button>
        <Button
          variant="outline"
          onClick={() => setConfirmUniqueOpen(true)}
          disabled={!activeCluster.can_merge || uniqueSelection.length < 2}
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          data-testid="mark-unique-btn"
        >
          <UserCheck className="mr-2 h-4 w-4" />
          These {uniqueSelection.length || ''} are different people
        </Button>
        <Button variant="outline" onClick={() => setActiveCluster(null)}>Cancel</Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="merge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm merge
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-stone-700 space-y-2">
                <p>
                  Keep <strong>{keeperRecord?.first_name} {keeperRecord?.last_name}</strong>{' '}
                  ({keeperRecord?.club_name || '—'})
                  and merge {losers.length} duplicate{losers.length === 1 ? '' : 's'} into it.
                </p>
                <p className="text-xs">
                  {losers.reduce((n, r) => n + (r.entries?.length || 0), 0)} entr
                  {losers.reduce((n, r) => n + (r.entries?.length || 0), 0) === 1 ? 'y' : 'ies'}
                  {' '}across these competitions will be re-pointed to the keeper:
                </p>
                <ul className="text-xs list-disc pl-5">
                  {[...new Set(losers.flatMap(r => (r.entries || []).map(e => e.competition_name)))].map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <p className="text-xs text-stone-500">
                  The merged records are kept in the database (audit trail) but hidden from
                  every list. This action is reversible if needed.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runMerge(); }}
              disabled={merging}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid="merge-confirm"
            >
              {merging ? 'Merging…' : 'Yes, merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmUniqueOpen} onOpenChange={setConfirmUniqueOpen}>
        <AlertDialogContent data-testid="mark-unique-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-700" />
              Confirm these are different people
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-stone-700 space-y-2">
                <p>You're confirming that the following {uniqueSelection.length} records represent <strong>different real people</strong>:</p>
                <ul className="text-xs list-disc pl-5">
                  {selectedNames.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
                <p className="text-xs text-stone-500">
                  This records {uniqueSelection.length * (uniqueSelection.length - 1) / 2}
                  {' '}pair-clearance{uniqueSelection.length * (uniqueSelection.length - 1) / 2 === 1 ? '' : 's'}.
                  These pairs will never re-cluster as duplicates again. Any future
                  record that comes in is still evaluated against each of them individually.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mark-unique-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runMarkUnique(); }}
              disabled={markingUnique}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="mark-unique-confirm"
            >
              {markingUnique ? 'Saving…' : 'Yes, mark unique'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <dt className="text-stone-500 w-24 shrink-0">{label}</dt>
      <dd className="text-stone-800">{children}</dd>
    </div>
  );
}
